import { Col, Row } from 'antd';
import { useCallback, useRef, useState } from 'react';
import RBCountryFilter from 'components/UniversalFilters/RBCountryFilter';
import AlertBar from '../../components/AlertBar';
import HeaderCard from '../../components/HeaderCard';
import SearchBox from '../../components/SearchBox';
import Select from '../../components/Select';
import CreateUserForm from './components/CreateUser';
import EditReportingDrawer from './components/drawer/EditReportingDrawer';
import UserDrawer from './components/drawer/UserDrawer';
import UsersTable from './components/table';
import useUsersInfo from './components/table/useUsersInfo';
import { USERS_ROLE } from '../../utils/constants';
import './styles.scss';

const DEFAULT_SORT_CONFIG = {
  column: 'email',
  order: 'asc',
};

const Users = () => {
  const [country, setCountry] = useState();
  const [role, setRole] = useState(null);
  const [searchedName, setSelectedName] = useState('');
  const [openEdit, setOpenEdit] = useState(false);
  const [openEditReportTo, setOpenEditReportTo] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);

  const searchRef = useRef();

  const handleSearchChange = (event) => {
    if (event.target.value === '') {
      setSelectedName(null);
    }
    searchRef.current = event.target.value;
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSelectedName(searchRef.current);
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleAction = (data, type) => {
    setSelectedRow(data);
    if (type === 'detail') {
      setOpenEdit(true);
    } else {
      setOpenEditReportTo(true);
    }
  };

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      setSortConfig(() => {
        const updatedConfig = {
          column: sorter?.column?.dataIndex,
          order: sorter?.order === 'ascend' ? 'asc' : 'desc',
        };
        return sorter?.order ? updatedConfig : DEFAULT_SORT_CONFIG;
      });
    },
    [setSortConfig],
  );

  const onFinishUpdate = () => {
    fetchUsers();
    setOpenEdit(false);
    setOpenEditReportTo(false);
  };

  const {
    isLoading,
    total,
    data: usersData,
    error,
    refetch: fetchUsers,
  } = useUsersInfo({
    currentPage,
    pageSize,
    country,
    role,
    searchedName,
    sortConfig,
  });

  return (
    <>
      {error && <AlertBar type="error" message={error} clearAfter={3000} />}
      <HeaderCard
        headerLabel="User Management"
        headerInfo="User overview appears here"
      />

      <Row>
        <Col span={24} className="table-container">
          <Row>
            <Col span={24}>
              <CreateUserForm refetchUsers={fetchUsers} />
            </Col>
          </Row>
          <Row className="filters">
            <SearchBox
              label={`Search user`}
              placeholder={`Search user by name/email`}
              onSearch={handleSearch}
              onChange={handleSearchChange}
            />
            <RBCountryFilter
              selectedCountry={country}
              onChange={setCountry}
              allowClear
            />
            <Select
              label={'Role'}
              placeholder={'Select role'}
              options={USERS_ROLE}
              value={role}
              onChange={setRole}
              allowClear={true}
            />
          </Row>
          <Row className="user-table">
            <UsersTable
              country={country}
              isLoading={isLoading}
              data={usersData}
              selectedRow={selectedRow}
              pagination={{ total, currentPage, pageSize, handlePageChange }}
              onTableChange={handleTableChange}
              handleAction={handleAction}
            />
          </Row>
        </Col>
      </Row>
      <EditReportingDrawer
        data={selectedRow}
        open={openEditReportTo}
        onClose={() => {
          setOpenEditReportTo(false);
          setSelectedRow(null);
        }}
        onFinish={onFinishUpdate}
      />
      <UserDrawer
        country={country}
        data={selectedRow}
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setSelectedRow(null);
        }}
        onSuccess={onFinishUpdate}
      />
    </>
  );
};

export default Users;
